/**
 * Booking Detail Page Component
 * /admin/bookings/:id
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import apiClient from '../api/client';

const BookingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [vehiclesLoading, setVehiclesLoading] = useState(false);
  const [vehiclesError, setVehiclesError] = useState('');
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [instructions, setInstructions] = useState('');
  const [startTripDialogOpen, setStartTripDialogOpen] = useState(false);
  const [startingTrip, setStartingTrip] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriverId, setSelectedDriverId] = useState('');
  const [startOdometer, setStartOdometer] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Asked for by the assign dialog rather than on page load: availability is
  // only meaningful against this booking's window, and a vehicle free ten
  // minutes ago may have been taken since. Memoised on `id` because the effect
  // below depends on it.
  const loadVehicles = useCallback(async () => {
    try {
      setVehiclesError('');
      setVehiclesLoading(true);
      const response = await apiClient.getBookingAvailableVehicles(id);
      setVehicles(response.results || []);
    } catch (err) {
      // Falling back to the plain vehicle list would quietly restore the old
      // behaviour - every vehicle offered, no reasons, refusal on assign. Say
      // so instead.
      setVehicles([]);
      setVehiclesError(err.message || 'Could not check vehicle availability');
    } finally {
      setVehiclesLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadBooking();
  }, [id]);

  useEffect(() => {
    if (assignDialogOpen) {
      loadVehicles();
    }
  }, [assignDialogOpen, loadVehicles]);

  useEffect(() => {
    if (startTripDialogOpen) {
      loadDrivers();
      // Set default odometer from vehicle if available
      if (booking?.vehicle_detail?.odometer) {
        setStartOdometer(booking.vehicle_detail.odometer.toString());
      }
    }
  }, [startTripDialogOpen, booking]);

  const loadBooking = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getBooking(id);
      setBooking(data);
    } catch (err) {
      setError(err.message || 'Failed to load booking');
    } finally {
      setLoading(false);
    }
  };

  const loadDrivers = async () => {
    try {
      const response = await apiClient.getDrivers({ status: 'active' });
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setDrivers(data);
    } catch (err) {
      console.error('Failed to load drivers:', err);
      setDrivers([]);
    }
  };

  const handleStartTrip = async () => {
    try {
      setStartingTrip(true);
      setError('');
      const response = await apiClient.startTripForBooking(
        id,
        selectedDriverId || null,
        startOdometer ? parseInt(startOdometer) : null
      );
      setSuccessMessage('Trip started successfully!');
      setStartTripDialogOpen(false);
      setSelectedDriverId('');
      setStartOdometer('');
      // Reload booking to get updated data
      await loadBooking();
    } catch (err) {
      setError(err.message || 'Failed to start trip');
    } finally {
      setStartingTrip(false);
    }
  };

  const handleAssignVehicle = async () => {
    if (!selectedVehicleId) {
      setError('Please select a vehicle');
      return;
    }

    try {
      setAssigning(true);
      setError('');
      const response = await apiClient.assignVehicleToBooking(id, selectedVehicleId, instructions);
      setSuccessMessage('Vehicle assigned successfully!');
      setAssignDialogOpen(false);
      setSelectedVehicleId('');
      setInstructions('');
      // Reload booking to get updated data
      await loadBooking();
    } catch (err) {
      setError(err.message || 'Failed to assign vehicle');
    } finally {
      setAssigning(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      confirmed: 'info',
      completed: 'success',
      cancelled: 'error',
      in_progress: 'primary',
    };
    return colors[status] || 'default';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `AED ${parseFloat(amount).toFixed(2)}`;
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !booking) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/bookings')}>
          Back to Bookings
        </Button>
      </Container>
    );
  }

  if (!booking) {
    return null;
  }

  const availableVehicleCount = vehicles.filter((vehicle) => vehicle.is_available).length;

  return (
    <Container maxWidth="lg">
      <Box mb={3} display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/bookings')}
            sx={{ mb: 1 }}
          >
            Back to Bookings
          </Button>
          <Typography variant="h4" component="h1" gutterBottom>
            Booking Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Booking Number: {booking.booking_reference || booking.booking_number || booking.id.substring(0, 8)}
          </Typography>
        </Box>
        <Box display="flex" gap={2}>
          {(!booking.vehicle && !booking.vehicle_detail) && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<DirectionsCarIcon />}
              onClick={() => setAssignDialogOpen(true)}
            >
              Assign Vehicle
            </Button>
          )}
          {booking.vehicle && (booking.status === 'assigned' || booking.status === 'confirmed') && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PlayArrowIcon />}
              onClick={() => setStartTripDialogOpen(true)}
            >
              Start Trip
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/admin/bookings/${booking.id}/edit`)}
          >
            Edit Booking
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Status & Service Type */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Status & Service
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip
                label={`Status: ${booking.status || 'pending'}`}
                color={getStatusColor(booking.status)}
                size="medium"
              />
              <Chip
                label={`Service: ${booking.service_type || '-'}`}
                color="default"
                size="medium"
              />
              {booking.trip_type && (
                <Chip
                  label={`Type: ${booking.trip_type}`}
                  color="default"
                  size="medium"
                />
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Customer Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Customer Information
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Type: {booking.customer_type === 'corporate' ? 'Corporate' : 'Direct'}
            </Typography>
            <Typography variant="body1" sx={{ mt: 1 }}>
              {booking.customer_name || '-'}
            </Typography>
            {booking.customer_phone && (
              <Typography variant="body2" color="text.secondary">
                Phone: {booking.customer_phone}
              </Typography>
            )}
            {booking.customer_email && (
              <Typography variant="body2" color="text.secondary">
                Email: {booking.customer_email}
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Itinerary. Both legs sit under the same booking reference - a round
            trip is one booking, not two (ADR-0003 D-01). */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {booking.journey_type === 'round_trip'
                ? 'Outbound Pickup Location'
                : 'Pickup Location'}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body1">
              {booking.pickup_location || '-'}
            </Typography>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {booking.journey_type === 'round_trip'
                ? 'Outbound Dropoff Location'
                : 'Dropoff Location'}
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body1">
              {booking.dropoff_location || '-'}
            </Typography>
          </Paper>
        </Grid>

        {booking.journey_type === 'round_trip' && (
          <>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Return Pickup Location
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body1">
                  {booking.return_pickup_location || '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {booking.return_datetime
                    ? new Date(booking.return_datetime).toLocaleString()
                    : 'Return time not set'}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Return Dropoff Location
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="body1">
                  {booking.return_dropoff_location || '-'}
                </Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Round-trip Price
                </Typography>
                <Divider sx={{ my: 1 }} />
                {booking.price_breakdown ? (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">
                        {booking.price_breakdown.base_price_label}
                      </Typography>
                      <Typography variant="body2">
                        {booking.price_breakdown.base_price}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">
                        Waiting charge ({booking.price_breakdown.waiting_hours} h &times;{' '}
                        {booking.price_breakdown.waiting_hourly_rate})
                      </Typography>
                      <Typography variant="body2">
                        {booking.price_breakdown.waiting_charge}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2">Total booking price</Typography>
                      <Typography variant="subtitle2">
                        {booking.price_breakdown.total_price}
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2">-</Typography>
                )}
              </Paper>
            </Grid>
          </>
        )}

        {/* Schedule */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Schedule
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Start Date & Time:
            </Typography>
            <Typography variant="body1">
              {formatDate(booking.start_datetime)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              End Date & Time:
            </Typography>
            <Typography variant="body1">
              {formatDate(booking.end_datetime)}
            </Typography>
            {booking.estimated_distance && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Estimated Distance:
                </Typography>
                <Typography variant="body1">
                  {booking.estimated_distance} km
                </Typography>
              </>
            )}
          </Paper>
        </Grid>

        {/* Pricing Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Pricing Information
            </Typography>
            <Divider sx={{ my: 1 }} />
            {booking.calculated_price && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Calculated Price (from Price List):
                </Typography>
                <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                  {formatCurrency(booking.calculated_price)}
                </Typography>
              </>
            )}
            {booking.route_price_detail && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: booking.calculated_price ? 0 : 0 }}>
                  Matched Route Price:
                </Typography>
                <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {booking.route_price_detail.price_list_name || 'Price List'}
                  </Typography>
                  {booking.route_price_detail.base_price && (
                    <Typography variant="body2" color="text.secondary">
                      Base Price: {formatCurrency(booking.route_price_detail.base_price)}
                    </Typography>
                  )}
                  {booking.route_price_detail.pickup_location?.name && (
                    <Typography variant="body2" color="text.secondary">
                      From: {booking.route_price_detail.pickup_location.name}
                    </Typography>
                  )}
                  {booking.route_price_detail.dropoff_location?.name && (
                    <Typography variant="body2" color="text.secondary">
                      To: {booking.route_price_detail.dropoff_location.name}
                    </Typography>
                  )}
                  {booking.route_price_detail.department && (
                    <Typography variant="body2" color="text.secondary">
                      Department: {booking.route_price_detail.department}
                    </Typography>
                  )}
                </Box>
              </>
            )}
            {booking.pricing_plan_detail && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Pricing Plan:
                </Typography>
                <Typography variant="body1">
                  {booking.pricing_plan_detail.name || booking.pricing_plan || '-'}
                  {booking.pricing_plan_detail.base_rate && (
                    <span style={{ color: '#666', marginLeft: '8px' }}>
                      ({formatCurrency(booking.pricing_plan_detail.base_rate)} per {booking.pricing_plan_detail.unit})
                    </span>
                  )}
                </Typography>
              </>
            )}
            {booking.cost_center_code && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Cost Center Code:
                </Typography>
                <Typography variant="body1">
                  {booking.cost_center_code}
                </Typography>
              </>
            )}
            {!booking.calculated_price && !booking.route_price_detail && !booking.pricing_plan_detail && (
              <Typography variant="body2" color="text.secondary">
                No pricing information available
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Vehicle Information */}
        {booking.vehicle && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Vehicle Information
              </Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body1">
                {booking.vehicle_detail?.plate_number || booking.vehicle_plate_number || booking.vehicle || '-'}
              </Typography>
              {booking.vehicle_detail && (
                <Typography variant="body2" color="text.secondary">
                  {booking.vehicle_detail.make} {booking.vehicle_detail.model}
                  {booking.vehicle_detail.year ? ` (${booking.vehicle_detail.year})` : ''}
                </Typography>
              )}
              {booking.driver_name && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Driver: {booking.driver_name}
                </Typography>
              )}
              {booking.dispatch_detail && (
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() => navigate(`/admin/dispatches/${booking.dispatch_detail.id}`)}
                >
                  View Dispatch Record
                </Button>
              )}
            </Paper>
          </Grid>
        )}

        {/* Additional Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Additional Information
            </Typography>
            <Divider sx={{ my: 1 }} />
            {booking.notes && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Notes:
                </Typography>
                <Typography variant="body1">
                  {booking.notes}
                </Typography>
              </>
            )}
            {booking.created_at && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Created At:
                </Typography>
                <Typography variant="body1">
                  {formatDate(booking.created_at)}
                </Typography>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Assign Vehicle Dialog */}
      <Dialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Assign Vehicle to Booking</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {booking?.start_datetime ? (
              <>
                Checked against{' '}
                <strong>{new Date(booking.start_datetime).toLocaleString()}</strong> to{' '}
                <strong>{new Date(booking.end_datetime).toLocaleString()}</strong>. Vehicles that
                cannot cover that period are listed with the reason and cannot be selected.
              </>
            ) : (
              'Select a vehicle to assign to this booking.'
            )}
          </DialogContentText>

          {vehiclesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {vehiclesError}
            </Alert>
          )}

          {!vehiclesLoading && !vehiclesError && availableVehicleCount === 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              No vehicle in this fleet is free for the whole booking period. Change the booking
              times, or release one of the vehicles listed below.
            </Alert>
          )}

          <FormControl fullWidth sx={{ mb: 2 }} disabled={vehiclesLoading}>
            <InputLabel>Select Vehicle</InputLabel>
            <Select
              value={selectedVehicleId}
              label="Select Vehicle"
              onChange={(e) => setSelectedVehicleId(e.target.value)}
              required
            >
              {vehiclesLoading && (
                <MenuItem value="" disabled>
                  Checking availability…
                </MenuItem>
              )}
              {vehicles.map((vehicle) => (
                <MenuItem
                  key={vehicle.id}
                  value={vehicle.id}
                  // Unavailable vehicles stay visible so the operator can see
                  // the whole fleet and why each one is out, but cannot pick
                  // one the server would refuse.
                  disabled={!vehicle.is_available}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 2,
                      width: '100%',
                    }}
                  >
                    <span>
                      {vehicle.plate_number} - {vehicle.make} {vehicle.model}
                      {vehicle.year ? ` (${vehicle.year})` : ''}
                    </span>
                    {vehicle.unavailable_reason && (
                      <Chip size="small" label={vehicle.unavailable_reason} />
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Instructions (Optional)"
            multiline
            rows={3}
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            placeholder="Add any special instructions for the driver..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={assigning}>
            Cancel
          </Button>
          <Button
            onClick={handleAssignVehicle}
            variant="contained"
            disabled={assigning || !selectedVehicleId}
            startIcon={assigning ? <CircularProgress size={20} /> : <DirectionsCarIcon />}
          >
            {assigning ? 'Assigning...' : 'Assign Vehicle'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Start Trip Dialog */}
      <Dialog
        open={startTripDialogOpen}
        onClose={() => setStartTripDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Start Trip</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Start the trip for this booking. The system will create a trip log and update the booking status to "active".
          </DialogContentText>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Driver (Optional)</InputLabel>
            <Select
              value={selectedDriverId}
              label="Select Driver (Optional)"
              onChange={(e) => setSelectedDriverId(e.target.value)}
            >
              <MenuItem value="">No Driver Selected</MenuItem>
              {drivers.map((driver) => (
                <MenuItem key={driver.id} value={driver.id}>
                  {driver.full_name || `${driver.first_name} ${driver.last_name}` || driver.id.substring(0, 8)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Start Odometer (Optional)"
            type="number"
            value={startOdometer}
            onChange={(e) => setStartOdometer(e.target.value)}
            placeholder="Enter starting odometer reading..."
            helperText={booking?.vehicle_detail?.odometer ? `Vehicle current odometer: ${booking.vehicle_detail.odometer.toLocaleString()} km` : ''}
            slotProps={{ htmlInput: { min: 0 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartTripDialogOpen(false)} disabled={startingTrip}>
            Cancel
          </Button>
          <Button
            onClick={handleStartTrip}
            variant="contained"
            color="success"
            disabled={startingTrip}
            startIcon={startingTrip ? <CircularProgress size={20} /> : <PlayArrowIcon />}
          >
            {startingTrip ? 'Starting...' : 'Start Trip'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BookingDetail;

